"use client";

import "@/app/(public)/shared/tokens/colors.css";
import "@/app/(public)/shared/tokens/typography.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { motion } from "motion/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { JOB_LISTINGS } from "./_logic/constants";

/**
 * The `/careers` listing: one card per open role, each linking to its own
 * `/careers/<slug>` description page.
 *
 * @returns The careers page.
 */
export default function Careers() {
  // Hooks
  const router = useRouter();

  // Render
  return (
    <div>
      <section className="container-top flex flex-col gap-16">
        <div className="flex flex-col gap-6">
          <h2 className="text-h2 text-landing-primary">Open positions</h2>
          <p className="text-body-lg text-landing-secondary max-w-2xl">
            Join us in building the future of event planning. We're looking for passionate individuals who want to make
            a difference.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          {Object.entries(JOB_LISTINGS).map(([slug, job], index) => (
            <motion.div
              key={slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-8 border border-landing-primary hover:border-landing-primary transition-colors duration-200"
            >
              <div className="flex flex-col lg:flex-row justify-between items-start gap-8">
                <div className="flex-1 flex flex-col gap-6">
                  <div className="flex gap-6 items-start">
                    <div className="w-16 h-16 relative shrink-0">
                      <Image src={job.image} alt={job.title} fill sizes="64px" className="object-cover" />
                    </div>
                    <div className="flex flex-col gap-3">
                      <Link href={`/careers/${slug}`} className="text-h4 text-landing-primary hover:underline">
                        <h3>{job.title}</h3>
                      </Link>
                      <p className="text-body-lg text-landing-secondary leading-relaxed">{job.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-3">
                    <h4 className="text-body font-semibold text-landing-primary">What you'll do:</h4>
                    <ul className="flex flex-col gap-2">
                      {job.responsibilities.map((responsibility, idx) => (
                        <li key={idx} className="text-body text-landing-secondary flex items-start gap-3">
                          <span className="flex items-center h-5 shrink-0">
                            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
                          </span>
                          {responsibility}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="shrink-0">
                  <Button variant="primary" color="primary" size="md" onClick={() => router.push(`/careers/${slug}`)}>
                    View role
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-6">
            <h2 className="text-h2 text-landing-primary">Don't see the right role?</h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              We're always looking for talented individuals to join our team. Send us your resume and let's start a
              conversation.
            </p>
          </div>
          <Button
            variant="outline"
            color="neutral"
            size="md"
            onClick={() => window.open("mailto:careers@nowadays.ai?subject=General Application", "_blank")}
          >
            Send us your resume
          </Button>
        </div>
      </section>
    </div>
  );
}
